import { useState } from 'react';
import type { Framework } from '../api.ts';
import {
  INCLUDED_FRAMEWORK_ID,
  INCLUDED_SCANS,
  SUITE_PRODUCT_ID,
  formatPence,
  orderedIds,
  planProblems,
  planTotal,
  scanLabel,
  scanPackOf,
  setScanPack,
  toggleFramework,
  toggleSuite,
  withBase,
} from '../billing.ts';
import { billingApi, type BillingProduct, type TenantPlan } from '../billingApi.ts';
import { message } from './format.ts';
import '../billing.css';

interface PlanPanelProps {
  tenantId: string;
  /** Active products, in the server's order. */
  products: readonly BillingProduct[];
  /** This client's plan as last saved, or null when the summary has no row for it. */
  plan: TenantPlan | null;
  /** The framework catalogue, only to name the entitlement in the catalogue's own words. */
  catalogue: readonly Framework[];
  /** `billing.manage`. Everybody else sees the same panel, read-only. */
  canManage: boolean;
  /** Whether saving changes a live subscription, which is worth saying before somebody does it. */
  hasSubscription: boolean;
  billingEnabled: boolean;
  onSaved: (plan: TenantPlan) => void;
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * What this one client costs a month, and what that buys it.
 *
 * An MSP re-bills each client separately, so the price is per client and the
 * total moves as a box is ticked. The rules the server enforces are applied
 * here as the operator clicks — the suite and single frameworks replace each
 * other, one scan pack at most — so there is no combination on screen that
 * Save would be refused for. The server checks again, and its answer is what
 * this panel shows afterwards: the entitlement below Save is the one it
 * derived, not one computed here.
 *
 * Everyone in the account can read it. Only an owner can change it, because a
 * tick here is a line on an invoice.
 */
export const PlanPanel = ({
  tenantId,
  products,
  plan,
  catalogue,
  canManage,
  hasSubscription,
  billingEnabled,
  onSaved,
}: PlanPanelProps) => {
  const savedIds = orderedIds(withBase(plan?.productIds ?? []), products);
  const [draft, setDraft] = useState<readonly string[]>(savedIds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const ofKind = (kind: BillingProduct['kind']) =>
    products.filter((product) => product.kind === kind);
  const base = ofKind('base')[0] ?? null;
  const frameworks = ofKind('framework');
  const suite = products.find((product) => product.id === SUITE_PRODUCT_ID) ?? null;
  const packs = ofKind('scans');

  const onSuite = draft.includes(SUITE_PRODUCT_ID);
  const pack = scanPackOf(draft, products);
  const total = planTotal(draft, products);
  const savedTotal = planTotal(savedIds, products);
  const problems = planProblems(draft, products);
  // A client the summary has no row for has never had a plan saved, and saving
  // the base alone is then a real change rather than a no-op.
  const changed = plan === null || !sameSet(draft, savedIds);

  const nameOf = (id: string): string =>
    catalogue.find((framework) => framework.id === id)?.name ?? id;
  const includedName = nameOf(INCLUDED_FRAMEWORK_ID);

  const edit = (next: string[]) => {
    setDraft(orderedIds(next, products));
    setStatus(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const response = await billingApi.setTenantPlan(tenantId, orderedIds(draft, products));
      setDraft(orderedIds(withBase(response.plan.productIds), products));
      setStatus(
        `Saved. This client now costs ${formatPence(response.plan.totalPence)} a month, excluding VAT.`,
      );
      onSaved(response.plan);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  const entitlement = plan?.entitlement ?? null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Plan</h2>
      </div>
      <div className="card-body">
        <p className="lead td-lead">
          What this client costs a month, excluding VAT. Each client is priced on its own, so
          what you see here is what you can re-bill them. Every control is still assessed whatever
          the plan: it decides which frameworks their report can be projected against and how
          often they are assessed.
        </p>

        {!canManage && (
          <p className="note">
            Only an account owner can change a client’s plan. You can see it here, read-only.
          </p>
        )}
        {canManage && !billingEnabled && (
          <p className="hint td-lead">
            Billing is not configured on this deployment, so saving a plan changes what this client
            may have without anything being invoiced.
          </p>
        )}
        {canManage && billingEnabled && hasSubscription && (
          <p className="hint td-lead">
            Saving changes this account’s subscription straight away. The difference for the rest
            of the month is prorated on the next invoice.
          </p>
        )}

        <fieldset className="bl-group" disabled={!canManage || saving}>
          <legend>Base</legend>
          <div className="field td-check bl-option">
            <label htmlFor="pl-base">
              <input
                id="pl-base"
                type="checkbox"
                checked
                disabled
                aria-describedby="pl-base-hint"
                onChange={() => undefined}
              />
              {base?.name ?? 'Client base'}
            </label>
            <span className="hint bl-price">{base === null ? '—' : formatPence(base.pricePence)}</span>
          </div>
          <p className="hint" id="pl-base-hint">
            Every client has it: {includedName} and {INCLUDED_SCANS} assessments a month, with
            drift, alerts and reports.
          </p>
        </fieldset>

        <fieldset className="bl-group" disabled={!canManage || saving}>
          <legend>Frameworks</legend>
          <div className="field td-check bl-option">
            <label htmlFor="pl-included">
              <input
                id="pl-included"
                type="checkbox"
                checked
                disabled
                onChange={() => undefined}
              />
              {includedName}
            </label>
            <span className="hint">Included</span>
          </div>

          {suite !== null && (
            <div className="field td-check bl-option">
              <label htmlFor="pl-suite">
                <input
                  id="pl-suite"
                  type="checkbox"
                  checked={onSuite}
                  aria-describedby="pl-suite-hint"
                  onChange={() => edit(toggleSuite(draft, products))}
                />
                {suite.name}
              </label>
              <span className="hint bl-price">{formatPence(suite.pricePence)}</span>
            </div>
          )}
          {suite !== null && (
            <p className="hint" id="pl-suite-hint">
              Every framework below, for less than buying them one by one. Choosing it replaces
              any you have ticked; untick it to choose them individually.
            </p>
          )}

          {frameworks.map((product) => (
            <div className="field td-check bl-option" key={product.id}>
              <label htmlFor={`pl-${product.id}`}>
                {/*
                  Ticked and disabled under the suite rather than unticked: the
                  client has every one of them, and an empty box beside a
                  framework they are paying for would say otherwise.
                */}
                <input
                  id={`pl-${product.id}`}
                  type="checkbox"
                  checked={onSuite || draft.includes(product.id)}
                  disabled={onSuite}
                  onChange={() => edit(toggleFramework(draft, product.id, products))}
                />
                {product.name}
              </label>
              <span className="hint bl-price">
                {onSuite ? 'In the suite' : formatPence(product.pricePence)}
              </span>
            </div>
          ))}
        </fieldset>

        <fieldset className="bl-group" disabled={!canManage || saving}>
          <legend>Assessments a month</legend>
          <div className="field td-check bl-option">
            <label htmlFor="pl-scans-included">
              <input
                id="pl-scans-included"
                type="radio"
                name="pl-scans"
                checked={pack === null}
                onChange={() => edit(setScanPack(draft, null, products))}
              />
              {scanLabel(null)}
            </label>
            <span className="hint bl-price">{formatPence(0)}</span>
          </div>
          {packs.map((product) => (
            <div className="field td-check bl-option" key={product.id}>
              <label htmlFor={`pl-${product.id}`}>
                <input
                  id={`pl-${product.id}`}
                  type="radio"
                  name="pl-scans"
                  checked={pack === product.id}
                  onChange={() => edit(setScanPack(draft, product.id, products))}
                />
                {scanLabel(product)}
              </label>
              <span className="hint bl-price">{formatPence(product.pricePence)}</span>
            </div>
          ))}
        </fieldset>

        {/*
          Polite and atomic, so a screen reader hears the whole new sentence
          each time a box changes the price, not just the digits that moved.
        */}
        <p className="bl-total" aria-live="polite" aria-atomic="true">
          <span>Monthly total for this client</span>
          <strong>{formatPence(total)}</strong>
          <span className="hint">
            excluding VAT{changed ? `, was ${formatPence(savedTotal)}` : ''}
          </span>
        </p>

        {canManage && (
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              aria-busy={saving}
              disabled={saving || !changed || problems.length > 0}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save plan'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={saving || !changed}
              onClick={() => edit(savedIds)}
            >
              Undo changes
            </button>
            <span className="status-inline" role="status">
              {error ?? (problems.length > 0 ? problems.join(' ') : status)}
            </span>
          </div>
        )}

        <h3 className="label">What this client has now</h3>
        {entitlement === null ? (
          <p className="note">No plan has been saved for this client yet.</p>
        ) : (
          <dl className="bl-entitlement">
            <dt>Frameworks</dt>
            <dd>
              {entitlement.frameworks === null
                ? 'Every framework, as your package allows'
                : entitlement.frameworks.map(nameOf).join(', ')}
            </dd>
            <dt>Assessments a month</dt>
            <dd>
              {entitlement.scansPerPeriod === null
                ? 'As your package allows'
                : String(entitlement.scansPerPeriod)}
            </dd>
          </dl>
        )}
      </div>
    </div>
  );
};
